<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="activeProfileTab === 'invited' ? allProspects : allCustomers"
      :single-expand="true"
      hide-default-footer
      :loading="loading"
      height="700px"
      fixed-header
      :options="customerTableOptions"
      :custom-sort="customSort"
      :custom-filter="customFilter"
      :show-expand="activeProfileTab !== 'invited'"
      item-key="id"
      @item-expanded="expandCustomer"
    >
      <template #[`item.firstName`]="{ item }">
        <td>
          <router-link 
            v-if="activeProfileTab !== 'invited'"
            :to="`/member/${item.id}`"
          >
            {{ item.firstName + " " + item.lastName }}
          </router-link>
          <span v-else>{{ item.firstName + " " + item.lastName }}</span>
        </td>
      </template>
      <template #[`item.zip`]="{ item }">
        <td>
          <span v-if="item.zip">
            {{ item.zip }}
          </span>
          <span v-else>--</span>
        </td>
      </template>
      <template #[`item.creationDate`]="{ item }">
        <td>
          {{ dateAndYear(item.creationDate) }}
        </td>
      </template>
      <template #[`item.hubCreationDate`]="{ item }">
        <td>
          {{ dateAndYear(item.hubCreationDate) }}
        </td>
      </template>
      <template #[`item.breakdownRisk`]="{ item }">
        <td>
          <span v-if="item.breakdownRisk" :class="[checkBreakdownRisk(item.breakdownRisk)]">
            {{ item.breakdownRisk }}
          </span>
          <span v-else>--</span>
        </td>
      </template>
      <template #[`item.currentLife`]="{ item }">
        <td>
          <span>
            {{ (item.currentLife * 100).toFixed(0) + "%" }}</span>
        </td>
      </template>
      <template #[`item.imagesCount`]="{ item }">
        <td>
          <span
            v-if="item.imagesCount && item.hubsCount"
            :class="[
              item.imagesCount === item.hubsCount
                ? 'green-text'
                : 'red-text',
            ]"
          >
            <span>{{ item.imagesCount + "/" + item.hubsCount }}</span>
          </span>
          <span v-else>--</span>
        </td>
      </template>
      <template #[`item.membershipType`]="{ item }">
        <td>
          {{
            item.membershipType.toLowerCase() === 'monitored'
              ? 'Detect'
              : 'Connect'
          }}
        </td>
      </template>
      <template #[`item.planName`]="{ item }">
        <td>
          {{
            item.planName
              ? item.planName
              : '--'
          }}
        </td>
      </template>
      <template #[`item.techFirstName`]="{ item }">
        <td>
          {{
            item.techFirstName
              ? `${item.techFirstName.slice(0, 1)}. ${item.techLastName}`
              : '--'
          }}
        </td>
      </template>
      <template #[`item.lastInvited`]="{ item }">
        <td>
          <span v-if="item.updateDate">
            {{ dateAndYear(item.updateDate) }}
          </span>
          <span v-else>--</span>
        </td>
      </template>
      <template #[`item.actions`]="{ item }">
        <td class="text-end">
          <button class="sac__button bg-primary" @click="toggleResendModal(item)">
            Resend
          </button>
        </td>
      </template>
      <template #expanded-item="{ item }">
        <td colspan="15" class="device-container">
          <div class="device-sub-container">
            <div class="device-header">
              <div>Serial Number</div>
              <div>Hub Status</div>
              <div>Date Activated</div>
              <div>Connection Method</div>
            </div>
            <div v-if="item.hubs === undefined" aria-colspan="6" class="loading-container">
              <img alt="loading" src="@shared/assets/img/general/loading.svg">
            </div>
            <div v-for="(el, idx) in item.hubs" v-else :key="idx" class="device-data">
              <div>
                {{ el.uId }}
              </div>
              <div :class="[el.onlineStatus ? 'green-text' : 'red-text']">
                <span v-if="el.onlineStatus">Online</span>
                <span v-else>Offline</span>
              </div>
              <div>{{ date(el.creationDate) }}</div>
              <div>{{ el.connectMethod }}</div>
            </div>
          </div>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import customerMixin from '@shared/mixins/customerMixin';
import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';
import CheckDateExpiry from '@shared/lib/general/CheckDateExpiry';

export default {
  name: 'OnboardedCustomers',
  mixins: [customerMixin, unitPreferencesMixin],
  computed: {
    headers() {
      let headers = [
        {
          text: 'Name',
          align: 'start',
          sortable: true,
          filterable: true,
          value: 'firstName',
        },
        {
          text: 'Zip',
          align: 'start',
          sortable: false,
          filterable: true,
          value: 'zip',
        },
        {
          text: 'Created',
          align: 'start',
          sortable: true,
          filterable: true,
          value: 'creationDate',
        },
        {
          text: 'Onboarded',
          align: 'start',
          sortable: true,
          filterable: true,
          value: 'hubCreationDate',
        },
        {
          text: 'Breakdown Risk',
          align: 'start',
          sortable: true,
          filterable: true,
          value: 'breakdownRisk',
        },
        {
          text: 'Filter Life',
          align: 'start',
          sortable: false,
          filterable: true,
          value: 'currentLife',
        },
        {
          text: 'Photo',
          align: 'start',
          sortable: false,
          filterable: true,
          value: 'imagesCount',
        },
        {
          text: 'Plan Type',
          align: 'start',
          sortable: true,
          filterable: false,
          value: 'membershipType',
        },
        {
          text: 'Plan',
          align: 'start',
          sortable: true,
          filterable: false,
          value: 'planName',
        },
        {
          text: 'Onboard Tech',
          align: 'start',
          sortable: false,
          filterable: true,
          value: 'techFirstName',
        },
        {
          text: '',
          sortable: false,
          filterable: false,
          value: 'data-table-expand',
        },
      ];
      if (this.activeProfileTab === 'invited') {
        headers = [
          {
            text: 'Name',
            align: 'start',
            sortable: true,
            filterable: true,
            value: 'firstName',
          },
          {
            text: 'Email',
            align: 'start',
            sortable: false,
            filterable: true,
            value: 'email',
          },
          {
            text: 'Phone',
            align: 'start',
            sortable: false,
            filterable: true,
            value: 'phone',
          },
          {
            text: 'Plan Invited to',
            align: 'start',
            sortable: false,
            filterable: true,
            value: 'planName',
          },
          {
            text: 'Last Invited',
            align: 'start',
            sortable: false,
            filterable: true,
            value: 'lastInvited',
          },
          {
            text: 'Actions',
            align: 'start',
            sortable: false,
            filterable: true,
            value: 'actions',
          },
        ];
      }
      return headers;
    },
  },
  methods: {
    isCardExpired(date) {
      return CheckDateExpiry(date);
    },
    customSort(items, index, isDesc) {
      if (index[0] !== this.customerTableOptions.sortBy[0] || isDesc[0] !== this.customerTableOptions.sortDesc[0]) {
        this.$store.dispatch('changeTableOption', {
          field: 'sortBy',
          value: index,
        });
        this.$store.dispatch('changeTableOption', {
          field: 'sortDesc',
          value: isDesc,
        });
        const get = async () => {
          this.$store.dispatch('getAllCustomers');
        };
        get();
      }
      return items;
    },
    expandCustomer({ item }) {
      const customerId = item.id;
      if (this.allCustomers.find((x) => x.id === customerId).hubs === undefined) {
        this.$store.dispatch('getCustomerHubs', customerId);
      }
    },
    customFilter(value, search, item) {
      const phone = item
        && item.phone
        && item.phone.split('-').join('');
      const s = search.split('-').join('').toLowerCase();
      const techName = `${item.techFirstName} ${item.techLastName}`.toLowerCase();
      return (
        value != null
        && s != null
        && typeof value === 'string'
        && (value.toString().toLowerCase().indexOf(s) !== -1
          || (phone && phone.toString().indexOf(s) !== -1)
          || (techName && techName.indexOf(s) !== -1))
      );
    },
    getHVACForUid(hvacs, uid) {
      return hvacs.find((h) => h.uid === uid);
    },
    toggleResendModal(prospect) {
      this.$store.dispatch('changeCustomerValue', {
        field: 'selectedProspect',
        value: prospect,
      });
      this.toggleActionModalCustomer(true, 'text-warning');
    }
  },
};
</script>
